import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  LayoutDashboard, Table2, PencilLine, Upload, FolderKanban, Users,
  HardHat, LogOut, Menu, X, ClipboardList, Building2, Layers, Settings as SettingsIcon,
  Database, SlidersHorizontal,
} from "lucide-react";
import { Button } from "./ui/button";

const NAV = [
  { to: "/executive", label: "Executive", icon: Building2, roles: ["admin"] },
  { to: "/executive-productivity", label: "Exec Productivity", icon: Layers, roles: ["admin"] },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "pm", "labor_manager"] },
  { to: "/report", label: "Productivity", icon: Table2, roles: ["admin", "pm", "labor_manager"] },
  { to: "/data-entry", label: "Data Entry", icon: PencilLine, roles: ["admin"] },
  { to: "/weekly", label: "Weekly Entry", icon: ClipboardList, roles: ["admin"] },
  { to: "/imports", label: "Imports", icon: Upload, roles: ["admin"] },
  { to: "/projects", label: "Projects", icon: FolderKanban, roles: ["admin"] },
  { to: "/procore-data", label: "Procore Data", icon: Database, roles: ["admin"] },
  { to: "/procore-mapping", label: "Field Mapping", icon: SlidersHorizontal, roles: ["admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin"] },
];

export default function AppLayout({ children }) {
  const { user, logout, role } = useAuth();
  const { projects, selectedId, select } = useProjects();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const items = NAV.filter((n) => n.roles.includes(role));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-[#0B192C] text-white sticky top-0 z-40 shadow-md border-b border-[#1E3E62]">
        <div className="px-3 sm:px-5 h-14 flex items-center gap-3">
          <button className="lg:hidden p-1" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-[#E8791A] flex items-center justify-center">
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight hidden sm:block">
              <div className="font-head font-bold text-base tracking-wide">CHARLES SERVICES</div>
              <div className="text-[10px] uppercase tracking-widest text-[#9DB4CE]">Labor Productivity</div>
            </div>
          </div>

          <div className="ml-2 sm:ml-6 w-44 sm:w-72" data-testid="project-selector">
            <Select value={selectedId} onValueChange={select}>
              <SelectTrigger className="h-9 bg-[#142843] border-[#1E3E62] text-white text-sm">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.id}`}>
                    {p.project_number} — {p.project_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#E8791A]">{user?.role_label}</div>
            </div>
            <Button
              variant="ghost" size="sm"
              className="text-white hover:bg-[#142843] hover:text-white"
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <nav
          className={`${open ? "block" : "hidden"} lg:block w-56 shrink-0 bg-white border-r border-slate-200 
            fixed lg:static inset-y-14 left-0 z-30 overflow-y-auto`}
          data-testid="sidebar-nav"
        >
          <div className="p-3 space-y-1">
            {items.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#0B192C] text-white"
                      : "text-slate-700 hover:bg-slate-100 hover:text-[#0B192C]"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>

        <main className="flex-1 min-w-0 p-3 sm:p-5">{children}</main>
      </div>
    </div>
  );
}
